/**
 * Homepage Destinations - Tab Panel Switching + Geo Restriction
 * Shows/hides per-tab panels when tab buttons are clicked.
 * Hides geo-restricted tabs based on visitor's real country (IP/timezone).
 */
(function () {
  'use strict';

  /**
   * Detect visitor's country from URL locale prefix and Shopify market.
   * Physical SIMs / SIM Cards only show on /en-au/ (AU market path).
   */
  function detectRealCountry(fallback) {
    // 1. Check URL path for locale prefix (e.g. /en-au/ → AU)
    var path = window.location.pathname.toLowerCase();
    var localeMatch = path.match(/^\/[a-z]{2}-([a-z]{2})\//);
    if (localeMatch) return localeMatch[1].toUpperCase();

    // 2. Fallback to Shopify market country (from data attribute)
    return (fallback || '').toUpperCase();
  }

  function init() {
    var section = document.querySelector('[data-section-type="homepage-tabs-destinations"]');
    if (!section) return;

    var tabBtns = section.querySelectorAll('[data-tab]');
    var panels = section.querySelectorAll('[data-panel]');
    if (!tabBtns.length || !panels.length) return;

    // Detect real visitor country
    var shopifyMarket = (section.getAttribute('data-geo-country') || '').toUpperCase();
    var realCountry = detectRealCountry(shopifyMarket);

    // Geo-restriction: hide tabs that don't match the visitor's real country
    // Skip in Shopify Theme Editor (design mode) so all tabs are visible for editing
    if (!window.__DESIGN_MODE && !(window.Shopify && window.Shopify.designMode)) {
      tabBtns.forEach(function (btn) {
        var restrict = (btn.getAttribute('data-geo-restrict') || '').toUpperCase();
        if (restrict && restrict !== realCountry) {
          btn.style.display = 'none';
          var panelId = btn.getAttribute('data-tab');
          var panel = section.querySelector('[data-panel="' + panelId + '"]');
          if (panel) panel.style.display = 'none';
        }
      });
    }

    // Ensure at least one tab is active after geo-filtering
    var hasActive = section.querySelector('.hp-tabs__btn--active:not([style*="display: none"])');
    if (!hasActive) {
      // The currently active tab was hidden — activate the first visible one
      panels.forEach(function (p) { p.classList.remove('is-active'); });
      tabBtns.forEach(function (t) { t.classList.remove('hp-tabs__btn--active'); });
      var firstVisible = section.querySelector('.hp-tabs__btn:not([style*="display: none"])');
      if (firstVisible) {
        firstVisible.classList.add('hp-tabs__btn--active');
        var firstPanelId = firstVisible.getAttribute('data-tab');
        var firstPanel = section.querySelector('[data-panel="' + firstPanelId + '"]');
        if (firstPanel) firstPanel.classList.add('is-active');
      }
    }

    // View More click handler — reveal hidden cards in batches, then link to collection
    var viewMoreBtns = section.querySelectorAll('.hp-view-more__btn');
    viewMoreBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tabKey = this.getAttribute('data-panel-target');
        var panel = section.querySelector('[data-panel="' + tabKey + '"]');
        if (!panel) return;

        var grid = panel.querySelector('.hp-destinations__grid');
        if (!grid) return;

        var step = parseInt(grid.getAttribute('data-step'), 10) || 8;
        var hidden = grid.querySelectorAll('.hp-card-wrap.hp-card--hidden');

        if (hidden.length === 0) {
          // All revealed — navigate to collection if available
          var url = this.getAttribute('data-collection-url');
          if (url) window.location.href = url;
          return;
        }

        var toReveal = Math.min(step, hidden.length);
        for (var i = 0; i < toReveal; i++) {
          (function (el, idx) {
            el.classList.remove('hp-card--hidden');
            el.classList.add('hp-card-wrap--revealing');
            el.style.animationDelay = (idx * 0.06) + 's';
            setTimeout(function () {
              el.classList.remove('hp-card-wrap--revealing');
              el.style.animationDelay = '';
            }, 600 + idx * 60);
          })(hidden[i], i);
        }

        // Update button label when no more hidden cards remain
        var remaining = grid.querySelectorAll('.hp-card-wrap.hp-card--hidden').length;
        if (remaining === 0) {
          var label = btn.querySelector('.hp-view-more__label');
          var finalLabel = btn.getAttribute('data-view-all-label');
          var url = btn.getAttribute('data-collection-url');
          if (url && label && finalLabel) {
            label.textContent = finalLabel;
            btn.classList.add('hp-view-more__btn--final');
          } else if (!url) {
            // No collection URL — hide the button
            btn.style.display = 'none';
          }
        }
      });
    });

    // Tab click handler
    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tabId = this.getAttribute('data-tab');

        tabBtns.forEach(function (t) {
          t.classList.remove('hp-tabs__btn--active');
          t.setAttribute('aria-selected', 'false');
        });
        this.classList.add('hp-tabs__btn--active');
        this.setAttribute('aria-selected', 'true');

        panels.forEach(function (panel) {
          panel.classList.remove('is-active');
        });

        var target = section.querySelector('[data-panel="' + tabId + '"]');
        if (!target) return;

        target.classList.add('is-active');

        // Register the newly-visible panel's deferred images with the
        // shared IntersectionObserver. This avoids the ~18-request burst
        // that bulk-hydration caused on tab switch — only viewport-near
        // images (within rootMargin: 300px) load immediately; the rest
        // wait for scroll.
        registerPanelImages(target);

        // Re-trigger stagger animations on cards
        var cards = target.querySelectorAll('.hp-card--animate, .hp-region-card--animate');
        cards.forEach(function (card) {
          card.style.opacity = '0';
          card.style.animation = 'none';
          void card.offsetHeight;
          card.style.animation = '';
          card.style.opacity = '';
        });
      });
    });
  }

  /**
   * Lazy-hydrate all <img data-src> on this page using a SHARED
   * IntersectionObserver. The destination-card snippet emits data-src for:
   *   - every card in inactive panels (hidden by display:none until tab click)
   *   - cards beyond index N in the active panel (below the fold)
   *
   * Native loading="lazy" doesn't help here because the browser still queues
   * the request the moment we set src; we leave src empty until the image
   * approaches the viewport.
   *
   * Module-scoped so the tab-click handler can also register newly-visible
   * panels' images with the same observer (instead of bulk-hydrating, which
   * would issue ~18 simultaneous requests on slow 4G).
   */
  var imageObserver = null;

  function hydrateImage(img) {
    var src = img.getAttribute('data-src');
    if (src) {
      img.src = src;
      img.removeAttribute('data-src');
    }
  }

  function bootDeferredImageObserver() {
    if (!('IntersectionObserver' in window)) {
      // No IO support — hydrate idle to avoid forever-empty images
      var hydrateAll = function () {
        document.querySelectorAll('img[data-src]').forEach(hydrateImage);
      };
      if (window.requestIdleCallback) requestIdleCallback(hydrateAll);
      else setTimeout(hydrateAll, 1500);
      return;
    }
    imageObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (!e.isIntersecting) continue;
        hydrateImage(e.target);
        imageObserver.unobserve(e.target);
      }
    }, { rootMargin: '300px 0px', threshold: 0.01 });
    document.querySelectorAll('img[data-src]').forEach(function (img) {
      imageObserver.observe(img);
    });
  }

  /**
   * Called from the tab-click handler. Registers freshly-visible panel
   * images with the shared observer instead of bulk-hydrating all at once.
   * The viewport-near images get loaded immediately by the observer; the
   * rest wait for scroll. Falls back to bulk hydration only if IO is
   * unavailable.
   */
  function registerPanelImages(panel) {
    if (!panel) return;
    var imgs = panel.querySelectorAll('img[data-src]');
    if (imageObserver) {
      imgs.forEach(function (img) { imageObserver.observe(img); });
    } else {
      imgs.forEach(hydrateImage);
    }
  }

  function boot() {
    init();
    bootDeferredImageObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
